import React, { useState, useEffect, useRef } from "react";
import "./AdminPanelRoomCard.css";

// Updated to Live Server URL
const API_BASE = "";

const AdminPanelRoomCard = () => {
  const [mode, setMode]               = useState("list");
  const [rooms, setRooms]             = useState([]);
  const [editingId, setEditingId]     = useState(null);
  const [loading, setLoading]         = useState(false);
  const [error, setError]             = useState(null);
  const fileInputRef                  = useRef(null);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [deletingId, setDeletingId]   = useState(null);

  const emptyRoom = {
    title: "", location: "", rating: "5.0", price: "",
    basePeople: 4, maxPeople: 8, extraAdultPrice: 400, kidPrice: 200,
    images: [], 
    videoUrl: "",
    timeSlots: [],
    currentSlot: "",
    slotsAvailable: 5,
    hasFood: true, hasFreeCancellation: true, hasDecor: true,
    discountSlots: [],
  };

  const [formData, setFormData] = useState(emptyRoom);

  const fetchRooms = async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await fetch(`${API_BASE}/api/rooms`);
      const data = await res.json();
      if (data.success) setRooms(data.data);
      else throw new Error(data.message);
    } catch (err) {
      setError("Failed to load rooms. Please check your connection.");
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchRooms(); }, []);

  const parseTimeToMinutes = (timeStr) => {
    try {
      const startTime = timeStr.split(' - ')[0].trim();
      let [time, period] = startTime.split(' ');
      let [hours, minutes] = time.split(':').map(Number);
      if (period === 'PM' && hours !== 12) hours += 12;
      if (period === 'AM' && hours === 12) hours = 0;
      return hours * 60 + minutes;
    } catch { return 0; }
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({ ...prev, [name]: type === 'checkbox' ? checked : value }));
  };

  const addSlot = () => {
    const newSlot = formData.currentSlot.trim();
    if (!newSlot) return;
    setFormData((prev) => {
      const updatedSlots = [...prev.timeSlots, newSlot].sort((a, b) => parseTimeToMinutes(a) - parseTimeToMinutes(b));
      return { ...prev, timeSlots: updatedSlots, currentSlot: "", slotsAvailable: updatedSlots.length };
    });
  };

  const handleKeyDown = (e) => { if (e.key === 'Enter') { e.preventDefault(); addSlot(); } };

  const removeSlot = (index) => {
    setFormData((prev) => {
      const newSlots = prev.timeSlots.filter((_, i) => i !== index);
      return { ...prev, timeSlots: newSlots, slotsAvailable: newSlots.length };
    });
  };

  const handleFiles = (files) => {
    Array.from(files).forEach((file) => {
      if (!file.type.startsWith('image/')) return;
      const reader = new FileReader();
      reader.onloadend = () => {
        setFormData((prev) => ({
          ...prev,
          images: [...prev.images, { url: reader.result, file, isNew: true }],
        }));
      };
      reader.readAsDataURL(file);
    });
  };

  const handleDrop = (e) => { e.preventDefault(); handleFiles(e.dataTransfer.files); };

  const removeImage = (index) => {
    setFormData((prev) => ({ ...prev, images: prev.images.filter((_, i) => i !== index) }));
  };
const saveRoom = async () => {
  if (!formData.title || !formData.price) {
    alert("Please fill required fields (Title and Price)!");
    return;
  }
  
  setLoading(true);
  
  try {
    const fd = new FormData();
    fd.append('title', formData.title);
    fd.append('location', formData.location);
    fd.append('rating', formData.rating);
    fd.append('price', formData.price);
    fd.append('basePeople', formData.basePeople);
    fd.append('maxPeople', formData.maxPeople);
    fd.append('extraAdultPrice', formData.extraAdultPrice);
    fd.append('kidPrice', formData.kidPrice);
    fd.append('videoUrl', formData.videoUrl || '');
    fd.append('hasFood', formData.hasFood);
    fd.append('hasFreeCancellation', formData.hasFreeCancellation);
    fd.append('hasDecor', formData.hasDecor);
    fd.append('timeSlots', JSON.stringify(formData.timeSlots));
    fd.append('discountSlots', JSON.stringify(formData.discountSlots));

    // ✅ Append images
    const newImages = formData.images.filter(img => img.isNew && img.file);
    console.log("📤 Sending images:", newImages.length);
    
    if (newImages.length > 0) {
      newImages.forEach(img => {
        fd.append('images', img.file);
        console.log("  -", img.file.name, "Size:", img.file.size);
      });
    } else {
      console.log("⚠️ No new images to upload");
    }

    if (editingId) {
      const keepUrls = formData.images.filter(img => !img.isNew).map(img => img.url);
      fd.append('keepImages', JSON.stringify(keepUrls));
      console.log("📋 Keeping existing images:", keepUrls.length);
    }

    const url = editingId ? `${API_BASE}/api/rooms/${editingId}` : `${API_BASE}/api/rooms`;
    const method = editingId ? 'PUT' : 'POST';

    console.log("🚀 Saving room:", { url, method, hasImages: newImages.length > 0 });

    const res = await fetch(url, { 
      method, 
      body: fd,
    });

    console.log("📥 Response status:", res.status);
    
    const data = await res.json();
    console.log("📥 Response data:", data);

    if (!data.success) {
      throw new Error(data.message || "Server error");
    }

    alert(editingId ? "✅ Theater updated successfully!" : "✅ Theater created successfully!");
    await fetchRooms();
    resetForm();
  } catch (err) {
    console.error("❌ SAVE ERROR:", err);
    alert(`Error: ${err.message}\n\nCheck server console for details`);
  } finally {
    setLoading(false);
  }
};

  const initiateDelete = (id) => { setDeletingId(id); setDeleteModalOpen(true); };

  const confirmDelete = async () => {
    try {
      const res  = await fetch(`${API_BASE}/api/rooms/${deletingId}`, { method: 'DELETE' });
      const data = await res.json();
      if (!data.success) throw new Error(data.message);
      await fetchRooms();
      setDeleteModalOpen(false);
    } catch (err) {
      console.error("Delete error:", err);
      alert(`Error deleting room: ${err.message}`);
    }
  };

  const startEdit = (room) => {
    setEditingId(room._id);
    setFormData({
      ...emptyRoom,
      ...room,
      images:      (room.images || []).map(url => ({ url, isNew: false })),
      timeSlots:   [...(room.timeSlots || [])].sort((a, b) => parseTimeToMinutes(a) - parseTimeToMinutes(b)),
      currentSlot: "",
    });
    setMode("form");
  };

  const resetForm = () => { setFormData({ ...emptyRoom }); setEditingId(null); setMode("list"); };

  return (
    <div className="admin-wrapper">
      <main className="admin-content">
        <header className="page-header">
          <div>
            <h2>Theater Management</h2>
            <p>Create, edit, or manage your theater listings.</p>
          </div>
          <button onClick={() => { resetForm(); setMode("form"); }} className="btn-primary">
            + New Theater
          </button>
        </header>

        {error && (
          <div style={{ background: '#fee2e2', border: '1px solid #fca5a5', borderRadius: 8, padding: '12px 16px', marginBottom: 16, color: '#dc2626' }}>
            {error}
          </div>
        )}

        {mode === "list" && (
          <div className="table-container card">
            {loading && <p style={{ textAlign: 'center', padding: 20, color: '#888' }}>Loading...</p>}
            <div className="table-responsive">
              <table>
                <thead>
                  <tr>
                    <th>Theater Name</th><th>Location</th><th>Base Price</th>
                    <th>Capacity</th><th>Time Slots</th><th className="action-col">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {!loading && rooms.length === 0 ? (
                    <tr><td colSpan="6" style={{ textAlign: 'center', padding: '60px 20px', color: '#888' }}>
                      No theaters found. Click "New Theater" to create one.
                    </td></tr>
                  ) : (
                    rooms.map((room) => (
                      <tr key={room._id}>
                        <td className="font-bold">{room.title}</td>
                        <td>{room.location}</td>
                        <td><span className="badge-price">₹{room.price}</span></td>
                        <td>{room.maxPeople} Pax</td>
                        <td>{Array.isArray(room.timeSlots) ? room.timeSlots.length : 0} slots</td>
                        <td>
                          <div className="action-buttons">
                            <button className="btn-icon btn-edit" onClick={() => startEdit(room)} title="Edit">
                              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/>
                                <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/>
                              </svg>
                            </button>
                            <button className="btn-icon btn-delete" onClick={() => initiateDelete(room._id)} title="Delete">
                              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                <polyline points="3 6 5 6 21 6"/>
                                <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/>
                              </svg>
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {mode === "form" && (
          <div className="form-panel card">
            <div className="form-title">
              <h3>{editingId ? "Edit Theater Details" : "Create New Theater"}</h3>
              <button onClick={resetForm} style={{ padding: "10px 22px", fontSize: "14px", fontWeight: "600", borderRadius: "8px", border: "1px solid #e53935", background: "linear-gradient(135deg, #ff4b2b, #ff416c)", color: "#fff", cursor: "pointer" }}>
                Cancel
              </button>
            </div>
            <div className="grid-inputs">
              <div className="input-group">
                <label>Name *</label>
                <input type="text" name="title" placeholder="e.g. Royal Cinema" value={formData.title} onChange={handleInputChange} />
              </div>
              <div className="input-group">
                <label>Location</label>
                <input type="text" name="location" placeholder="e.g. Downtown" value={formData.location} onChange={handleInputChange} />
              </div>
              <div className="input-group">
                <label>Rating</label>
                <input type="text" name="rating" placeholder="e.g. 4.5" value={formData.rating} onChange={handleInputChange} />
              </div>
              <div className="input-group">
                <label>Base Price (₹) *</label>
                <input type="number" name="price" placeholder="0.00" value={formData.price} onChange={handleInputChange} />
              </div>
              <div className="input-group">
                <label>Max Capacity</label>
                <input type="number" name="maxPeople" value={formData.maxPeople} onChange={handleInputChange} />
              </div>
              <div className="input-group">
                <label>Included People</label>
                <input type="number" name="basePeople" value={formData.basePeople} onChange={handleInputChange} />
              </div>
              <div className="input-group">
                <label>Extra Adult Price (₹)</label>
                <input type="number" name="extraAdultPrice" value={formData.extraAdultPrice} onChange={handleInputChange} />
              </div>
              <div className="input-group">
                <label>Kids Cost (₹)</label>
                <input type="number" name="kidPrice" value={formData.kidPrice} onChange={handleInputChange} />
              </div>
              <div className="input-group" style={{ gridColumn: "1 / -1" }}>
                <label>Video URL (Optional)</label>
                <input type="text" name="videoUrl" placeholder="https://example.com/video.mp4" value={formData.videoUrl} onChange={handleInputChange} />
              </div>
            </div>

            <div className="section-title">Features</div>
            <div style={{ display: 'flex', gap: '20px', marginBottom: '20px', flexWrap: 'wrap' }}>
              {[['hasFood', 'Food Available'], ['hasFreeCancellation', 'Free Cancellation'], ['hasDecor', 'Decor Available']].map(([name, label]) => (
                <label key={name} style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                  <input type="checkbox" name={name} checked={formData[name]} onChange={handleInputChange} />
                  <span>{label}</span>
                </label>
              ))}
            </div>

            <div className="section-title">Gallery Images</div>
            <div className="upload-zone" onDragOver={(e) => e.preventDefault()} onDrop={handleDrop} onClick={() => fileInputRef.current.click()}>
              <div className="upload-icon">
                <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                  <rect x="3" y="3" width="18" height="18" rx="2" ry="2"/>
                  <circle cx="8.5" cy="8.5" r="1.5"/>
                  <polyline points="21 15 16 10 5 21"/>
                </svg>
              </div>
              <h3>Select From Computer</h3>
              <p>or drag and drop images here</p>
              <input type="file" multiple hidden ref={fileInputRef} accept="image/*" onChange={(e) => handleFiles(e.target.files)} />
            </div>
            <div className="preview-grid">
              {formData.images.map((img, index) => (
                <div key={index} className="img-box">
                  <img src={img.url} alt="preview" />
                  {img.isNew && <span style={{ position: 'absolute', top: 4, left: 4, background: '#16a34a', color: '#fff', fontSize: 10, padding: '2px 6px', borderRadius: 4 }}>NEW</span>}
                  <button className="remove-btn" onClick={() => removeImage(index)}>×</button>
                </div>
              ))}
            </div>

            <div className="section-title">Time Slots ({formData.timeSlots.length} added)</div>
            <div className="slot-section">
              <div className="slot-input-wrap">
                <input type="text" name="currentSlot" placeholder="e.g. 10:00 AM - 12:00 PM" value={formData.currentSlot} onChange={handleInputChange} onKeyDown={handleKeyDown} />
                <button type="button" onClick={addSlot} className="btn-slot">+ Add Slot</button>
              </div>
              <div className="slot-list">
                {formData.timeSlots.length === 0 && <p style={{ fontSize: '13px', color: '#999' }}>No slots added yet.</p>}
                {formData.timeSlots.map((slot, index) => (
                  <div key={index} className="slot-tag">
                    {slot}
                    <button className="slot-remove" onClick={() => removeSlot(index)}>×</button>
                  </div>
                ))}
              </div>
            </div>

            <div className="form-footer">
              <button className="btn-save" onClick={saveRoom} disabled={loading}>
                {loading ? 'Saving...' : 'Save Theater'}
              </button>
            </div>
          </div>
        )}
      </main>

      {deleteModalOpen && (
        <div className="modal-backdrop">
          <div className="modal-card">
            <h3>Delete Item?</h3>
            <p>You are about to permanently delete this theater. This action cannot be undone.</p>
            <div className="modal-actions">
              <button className="btn-secondary" onClick={() => setDeleteModalOpen(false)}>Cancel</button>
              <button className="btn-danger" onClick={confirmDelete}>Delete Permanently</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminPanelRoomCard;